/**
 * OutpostCreationModal.jsx - Modal for creating a new outpost on a tile
 * Allows player to name their outpost and see construction costs/time
 */

import React, { useState } from 'react';
import { useGame } from '../../context/GameContext';
import { getOutpostCost, OUTPOST_CONFIG } from '../../data/outposts';
import { BIOMES } from '../../data/world-map/biomes';
import { resources } from '../../data/resources';
import './OutpostCreationModal.css';

// Format time in minutes and seconds
const formatTime = (ms) => {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  }
  return `${seconds}s`;
};

export function OutpostCreationModal({ nodeId, onClose, onSuccess }) {
  const { worldMap, playerResources, createOutpost, triggerGuidedTourAction } = useGame();
  const [outpostName, setOutpostName] = useState('');
  const [error, setError] = useState(null);

  const tileData = worldMap.discoveredNodes[nodeId];
  const biomeId = tileData?.biome || 'default';
  const biome = BIOMES[biomeId];
  const cost = getOutpostCost(biomeId);

  // Count current outposts
  const currentOutpostCount = Object.keys(worldMap.outposts || {}).length;
  const maxOutposts = OUTPOST_CONFIG.maxOutpostsTotal;
  const atCapacity = currentOutpostCount >= maxOutposts;

  // Check affordability
  const canAfford = Object.entries(cost).every(
    ([res, amt]) => (playerResources[res] || 0) >= amt
  );

  // Can create if both affordable and not at capacity
  const canCreate = canAfford && !atCapacity;

  // Handle create button click
  const handleCreate = () => {
    const name = outpostName.trim() || `${biome?.name || 'New'} Outpost`;
    const result = createOutpost(nodeId, name);

    if (result.success) {
      // Signal action for guided tour
      triggerGuidedTourAction?.('outpostCreated');
      if (onSuccess) onSuccess(result.outpostId);
      onClose();
    } else {
      setError(result.reason);
    }
  };

  return (
    <div className="ocm-overlay" onClick={onClose}>
      <div className="ocm-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="ocm-header">
          <span className="ocm-icon">🏰</span>
          <h2>Create Outpost</h2>
          <span className="ocm-count">{currentOutpostCount}/{maxOutposts}</span>
        </div>

        {/* Tile Info */}
        <div className="ocm-section ocm-tile-info">
          <div className="ocm-label">Location</div>
          <div className="ocm-tile">
            <span className="ocm-biome-icon">{biome?.icon || '🗺️'}</span>
            <span className="ocm-biome-name">{biome?.name || 'Unknown Biome'}</span>
          </div>
        </div>

        {/* Name Input */}
        <div className="ocm-section">
          <div className="ocm-label">Outpost Name</div>
          <input
            type="text"
            className="ocm-name-input"
            placeholder={`${biome?.name || 'New'} Outpost`}
            value={outpostName}
            onChange={(e) => setOutpostName(e.target.value)}
            maxLength={30}
            autoFocus
          />
        </div>

        {/* Construction Cost */}
        <div className="ocm-section">
          <div className="ocm-label">Construction Cost</div>
          <div className="ocm-costs">
            {Object.entries(cost).map(([resourceId, amount]) => {
              const resourceInfo = resources[resourceId] || { name: resourceId, icon: '📦' };
              const have = playerResources[resourceId] || 0;
              const hasEnough = have >= amount;

              return (
                <div
                  key={resourceId}
                  className={`ocm-cost-item ${hasEnough ? '' : 'insufficient'}`}
                >
                  <span className="ocm-cost-icon">{resourceInfo.icon}</span>
                  <span className="ocm-cost-amount">{amount}</span>
                  <span className="ocm-cost-name">{resourceInfo.name}</span>
                  <span className={`ocm-cost-have ${hasEnough ? 'has' : 'lacks'}`}>
                    ({have})
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Construction Time */}
        <div className="ocm-section">
          <div className="ocm-label">Construction Time</div>
          <div className="ocm-time">
            <span className="ocm-time-icon">⏱️</span>
            <span className="ocm-time-value">{formatTime(OUTPOST_CONFIG.constructionTime)}</span>
          </div>
        </div>

        {/* Outpost Features Preview */}
        <div className="ocm-section ocm-features">
          <div className="ocm-label">Outpost Features</div>
          <ul className="ocm-feature-list">
            <li><span className="feature-icon">🏗️</span> <strong>{OUTPOST_CONFIG.maxBuildingSlots} building slots</strong> - Each grants +5 panel capacity</li>
            <li><span className="feature-icon">⚔️</span> <strong>Hire mercenaries</strong> - Form squads to auto-run dungeons</li>
            <li><span className="feature-icon">🛤️</span> <strong>Trade route</strong> - Connect to your kingdom</li>
            <li><span className="feature-icon">🔮</span> <strong>Instant teleport</strong> - Travel here from anywhere</li>
          </ul>
        </div>

        {/* At Capacity Warning */}
        {atCapacity && (
          <div className="ocm-error">
            Maximum outposts reached ({maxOutposts}/{maxOutposts})
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="ocm-error">
            {error}
          </div>
        )}

        {/* Buttons */}
        <div className="ocm-buttons">
          <button className="ocm-btn ocm-cancel" onClick={onClose}>
            Cancel
          </button>
          <button
            className={`ocm-btn ocm-create ${canCreate ? '' : 'disabled'}`}
            onClick={handleCreate}
            disabled={!canCreate}
          >
            {atCapacity ? 'At Capacity' : canAfford ? 'Build Outpost' : 'Cannot Afford'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default OutpostCreationModal;
